import { Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { logEvent } from 'firebase/analytics';
import { analytics } from '@services/firebase';
import { Box } from '@mui/material';
import AppBar from './components/common/AppBar';
import Footer from './components/common/Footer';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import CreateEventPage from './pages/CreateEventPage';
import EditEventPage from './pages/EditEventPage';
import EventReviewPage from './pages/EventReviewPage';
import ProfilePage from './pages/ProfilePage';
import NotificationsPage from './pages/NotificationsPage';
import SchedulePage from './pages/SchedulePage';
import AnalyticsPage from './pages/AnalyticsPage';
import SupportPage from './pages/SupportPage';
import HelpPage from './pages/HelpPage';
import VolunteersPage from './pages/VolunteersPage';
import CommunicationsPage from './pages/CommunicationsPage';
import OrgSettingsPage from './pages/OrgSettingsPage';
import AdminOrganizationsPage from './pages/AdminOrganizationsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminSupportPage from './pages/AdminSupportPage';
import ActivityPage from './pages/ActivityPage';
import BlockedEntitiesPage from './pages/admin/BlockedEntitiesPage';
import SignInLogsPage from './pages/admin/SignInLogsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AuthActionPage from './pages/AuthActionPage';
import ProtectedRoute from './components/common/ProtectedRoute';

// Placeholder page component for pages not yet built
const PlaceholderPage = ({ title }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 'calc(100vh - 200px)',
      flexDirection: 'column',
      gap: 2,
      py: 4,
    }}
  >
    <h1>{title}</h1>
    <p>Coming soon...</p>
  </Box>
);

function App() {
  const location = useLocation();

  useEffect(() => {
    if (analytics) {
      logEvent(analytics, 'page_view', { page_path: location.pathname });
    }
  }, [location.pathname]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <AppBar />

      <Box sx={{ flex: 1 }}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/action" element={<AuthActionPage />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/create"
            element={
              <ProtectedRoute roles={['org-admin', 'platform-admin']}>
                <CreateEventPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/:eventId/edit"
            element={
              <ProtectedRoute roles={['org-admin', 'platform-admin']}>
                <EditEventPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/:eventId"
            element={
              <ProtectedRoute>
                <EventReviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedule"
            element={
              <ProtectedRoute roles={['volunteer']}>
                <SchedulePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute roles={['org-admin', 'platform-admin']}>
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/volunteers"
            element={
              <ProtectedRoute roles={['org-admin', 'platform-admin']}>
                <VolunteersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/communications"
            element={
              <ProtectedRoute roles={['org-admin', 'platform-admin']}>
                <CommunicationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/org-settings"
            element={
              <ProtectedRoute roles={['org-admin', 'platform-admin']}>
                <OrgSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/organizations"
            element={
              <ProtectedRoute roles={['platform-admin']}>
                <AdminOrganizationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute roles={['platform-admin']}>
                <AdminUsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/support"
            element={
              <ProtectedRoute roles={['platform-admin']}>
                <AdminSupportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/blocked"
            element={
              <ProtectedRoute roles={['platform-admin']}>
                <BlockedEntitiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/signin-logs"
            element={
              <ProtectedRoute roles={['platform-admin']}>
                <SignInLogsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/activity"
            element={
              <ProtectedRoute roles={['org-admin', 'platform-admin']}>
                <ActivityPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/org-settings/:orgId"
            element={
              <ProtectedRoute roles={['platform-admin']}>
                <OrgSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsAndConditionsPage />} />

          {/* 404 */}
          <Route path="*" element={<PlaceholderPage title="404 - Not Found" />} />
        </Routes>
      </Box>

      <Footer />
    </Box>
  );
}

export default App;
